import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ShieldCheck,
  Building2,
  CheckCircle2,
  Smartphone,
  CreditCard,
  QrCode,
  Lock,
  Download,
  Calendar,
  Check,
  Receipt,
  Sparkles,
  PenTool,
  RotateCcw,
  HelpCircle,
  ChevronRight,
  Send,
  AlertCircle,
  Phone,
  Mail,
  Clock,
  MapPin,
  MessageCircle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { PaymentAgreement, AgreementStatus } from '@/components/dashboard/agreements/PaymentAgreementTypes';
import { PaymentAgreementPdfService } from '@/services/paymentAgreementPdfService';
import { PaymentAgreementDatabaseService } from '@/services/paymentAgreementDatabaseService';
import { toast } from 'sonner';

const STORAGE_KEY = 'legal_notifications_agreements_v1';

// Función para sanitizar y extraer el token limpio sin residuos de WhatsApp o markdown
const sanitizeToken = (raw: string | null | undefined): string => {
  if (!raw) return '';
  try {
    const decoded = decodeURIComponent(raw).trim();
    // 1. Extraer patrón 'tok_...'
    const tokenMatch = decoded.match(/(tok_[a-zA-Z0-9_-]+)/i);
    if (tokenMatch && tokenMatch[1]) {
      return tokenMatch[1];
    }
    // 2. Extraer 'AGR-...'
    const idMatch = decoded.match(/(AGR-[a-zA-Z0-9_-]+)/i);
    if (idMatch && idMatch[1]) {
      return idMatch[1];
    }
    // 3. Primer fragmento antes de espacios o caracteres de corte
    return decoded.split(/[\s,;+_]/)[0].replace(/[^a-zA-Z0-9_-]/g, '');
  } catch {
    return (raw || '').split(/\s+/)[0];
  }
};

// Acuerdos de ejemplo realistas
const FALLBACK_AGREEMENTS: PaymentAgreement[] = [
  {
    id: 'AGR-904123',
    copropiedad_id: 'default',
    copropiedad_nombre: 'Conjunto Residencial Torres del Parque',
    copropiedad_nit: '900.852.147-3',
    administrador_nombre: 'Administración PH - Dra. Claudia Morales',
    administrador_telefono: '(601) 745-8920',
    administrador_whatsapp: '3184567890',
    administrador_email: 'cartera@torresdelparque.com',
    administrador_horario: 'Lunes a Viernes 8:00 AM - 12:00 PM / 2:00 PM - 5:00 PM | Sábados 9:00 AM - 1:00 PM',
    administrador_oficina: 'Oficina de Administración (Torre 1, Mezzanine - Portería Principal)',
    unidad_privada: 'Torre 2 Apto 504',
    propietario_nombre: 'Carlos Eduardo Restrepo',
    propietario_id: '79845123',
    telefono: '3104567890',
    email: 'carlos.restrepo@gmail.com',

    deuda_total_inicial: 3200000,
    capital_original: 2600000,
    intereses_originales: 600000,
    porcentaje_condonacion_intereses: 50,
    intereses_condonados: 300000,
    deuda_pactada: 2900000,

    cuota_inicial_monto: 725000,
    cuota_inicial_porcentaje: 25,
    cuota_inicial_pagada: false, // Inicia pendiente para que el deudor firme y pague
    cuota_inicial_fecha_limite: '2026-11-05',

    saldo_a_financiar: 2175000,
    numero_cuotas: 6,
    valor_cuota_mensual: 362500,
    tasa_financiacion_mensual: 0,
    dia_pago_mensual: 10,
    fecha_inicio: '2026-11-10',

    estado: 'enviado_al_deudor', // Estado inicial de propuesta formal
    cuotas_pagadas: 0,
    cuotas_totales: 6,
    total_recaudado: 0,
    saldo_restante: 2900000,
    proximo_vencimiento: '2026-11-05',

    magic_token: 'tok_carlos_restrepo_t2_504',
    portal_url: `${window.location.origin}/portal-deudor/torres-del-parque/tok_carlos_restrepo_t2_504`,
    firmado_digitalmente_at: undefined,

    cuotas_detalle: [
      { numero: 1, fecha_vencimiento: '2026-11-10', capital: 362500, interes: 0, total_cuota: 362500, estado: 'pendiente' },
      { numero: 2, fecha_vencimiento: '2026-12-10', capital: 362500, interes: 0, total_cuota: 362500, estado: 'pendiente' },
      { numero: 3, fecha_vencimiento: '2027-01-10', capital: 362500, interes: 0, total_cuota: 362500, estado: 'pendiente' },
      { numero: 4, fecha_vencimiento: '2027-02-10', capital: 362500, interes: 0, total_cuota: 362500, estado: 'pendiente' },
      { numero: 5, fecha_vencimiento: '2027-03-10', capital: 362500, interes: 0, total_cuota: 362500, estado: 'pendiente' },
      { numero: 6, fecha_vencimiento: '2027-04-10', capital: 362500, interes: 0, total_cuota: 362500, estado: 'pendiente' }
    ],
    created_at: '2026-10-26T10:00:00Z',
    updated_at: '2026-10-26T10:00:00Z'
  },
  {
    id: 'AGR-881942',
    copropiedad_id: 'default',
    copropiedad_nombre: 'Conjunto Residencial Torres del Parque',
    copropiedad_nit: '900.852.147-3',
    administrador_nombre: 'Administración PH - Dra. Claudia Morales',
    unidad_privada: 'Torre 1 Apto 302',
    propietario_nombre: 'Marcela Gómez Duarte',
    propietario_id: '52891456',
    telefono: '3157891234',
    email: 'marcela.gomez@hotmail.com',

    deuda_total_inicial: 1850000,
    capital_original: 1500000,
    intereses_originales: 350000,
    porcentaje_condonacion_intereses: 70,
    intereses_condonados: 245000,
    deuda_pactada: 1605000,

    cuota_inicial_monto: 400000,
    cuota_inicial_porcentaje: 25,
    cuota_inicial_pagada: true,
    cuota_inicial_fecha_limite: '2026-09-20',
    cuota_inicial_referencia: 'PSE-39201',

    saldo_a_financiar: 1205000,
    numero_cuotas: 4,
    valor_cuota_mensual: 301250,
    tasa_financiacion_mensual: 0,
    dia_pago_mensual: 15,
    fecha_inicio: '2026-09-15',

    estado: 'en_mora',
    cuotas_pagadas: 1,
    cuotas_totales: 4,
    total_recaudado: 701250, // Cuota inicial + cuota 1
    saldo_restante: 903750,
    proximo_vencimiento: '2026-10-15',
    mora_dias: 11,

    magic_token: 'tok_marcela_gomez_t1_302',
    portal_url: `${window.location.origin}/portal-deudor/torres-del-parque/tok_marcela_gomez_t1_302`,
    firmado_digitalmente_at: '2026-09-15T09:00:00Z',

    cuotas_detalle: [
      { numero: 1, fecha_vencimiento: '2026-09-15', capital: 301250, interes: 0, total_cuota: 301250, estado: 'pagada', fecha_pago: '2026-09-15', referencia_pago: 'PSE-39201' },
      { numero: 2, fecha_vencimiento: '2026-10-15', capital: 301250, interes: 0, total_cuota: 301250, estado: 'vencida' },
      { numero: 3, fecha_vencimiento: '2026-11-15', capital: 301250, interes: 0, total_cuota: 301250, estado: 'pendiente' },
      { numero: 4, fecha_vencimiento: '2026-12-15', capital: 301250, interes: 0, total_cuota: 301250, estado: 'pendiente' }
    ],
    created_at: '2026-09-15T09:00:00Z',
    updated_at: '2026-10-16T08:00:00Z'
  }
];

export const PublicDebtorPortalPage: React.FC = () => {
  const { conjuntoSlug, token: urlToken } = useParams<{ conjuntoSlug?: string; token?: string }>();
  const [searchParams] = useSearchParams();
  const queryToken = searchParams.get('token');
  const activeToken = urlToken || queryToken;

  const [agreement, setAgreement] = useState<PaymentAgreement | null>(null);
  const [allAgreements, setAllAgreements] = useState<PaymentAgreement[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Estados de firma digital
  const [signatureMode, setSignatureMode] = useState<'otp' | 'canvas'>('otp');
  const [otpCode, setOtpCode] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isSigned, setIsSigned] = useState(false);
  const [signatureTimestamp, setSignatureTimestamp] = useState<string>('');

  // Canvas de firma manuscrita
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);

  // Estados de pasarela de pago (Wompi, PSE, Nequi, Bancos)
  const [paymentMethod, setPaymentMethod] = useState<'wompi' | 'pse' | 'nequi' | 'barras'>('wompi');
  const [wompiSubMethod, setWompiSubMethod] = useState<'bancolombia' | 'pse' | 'nequi'>('bancolombia');
  const [selectedBank, setSelectedBank] = useState('Bancolombia');
  const [payerEmail, setPayerEmail] = useState('');
  const [nequiPhone, setNequiPhone] = useState('');
  const [isWaitingNequiPush, setIsWaitingNequiPush] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Comprobante de pago emitido
  const [paymentReceipt, setPaymentReceipt] = useState<{
    referencia: string;
    fecha: string;
    monto: number;
    metodo: string;
    banco: string;
  } | null>(null);

  // Modal de contacto con administración
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Cargar acuerdos de almacenamiento local o iniciales
  useEffect(() => {
    setIsLoading(true);
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      let list: PaymentAgreement[] = FALLBACK_AGREEMENTS;
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          list = parsed;
        }
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(FALLBACK_AGREEMENTS));
      }

      // Enlazar los canales oficiales de administración ingresados por el administrador
      const adminSettings = PaymentAgreementDatabaseService.getAdminContactSettings();
      list = list.map((a) => ({
        ...a,
        administrador_nombre: a.administrador_nombre || adminSettings.administrador_nombre,
        administrador_whatsapp: a.administrador_whatsapp || adminSettings.administrador_whatsapp,
        administrador_telefono: a.administrador_telefono || adminSettings.administrador_telefono,
        administrador_email: a.administrador_email || adminSettings.administrador_email,
        administrador_horario: a.administrador_horario || adminSettings.administrador_horario,
        administrador_oficina: a.administrador_oficina || adminSettings.administrador_oficina
      }));

      setAllAgreements(list);

      // Sanitizar token recibido
      const cleanToken = sanitizeToken(activeToken).toLowerCase();
      const rawLower = (activeToken || '').toLowerCase();
      const cleanConjunto = (conjuntoSlug || '').toLowerCase().trim();

      let matched: PaymentAgreement | undefined = undefined;

      // 1. Si viene token específico
      if (cleanToken && (cleanToken.startsWith('tok_') || cleanToken.startsWith('agr-') || cleanToken.length > 8)) {
        matched = list.find((a) => {
          const aMagic = (a.magic_token || '').toLowerCase();
          const aId = (a.id || '').toLowerCase();
          return (
            aMagic === cleanToken ||
            aId === cleanToken ||
            (cleanToken.length >= 5 && aMagic.includes(cleanToken)) ||
            (aMagic.length >= 5 && cleanToken.includes(aMagic)) ||
            (aMagic.length >= 5 && rawLower.includes(aMagic)) ||
            (aId.length >= 4 && rawLower.includes(aId))
          );
        });
      }

      // 2. Si vino conjuntoSlug o token representaba el conjunto
      if (!matched && (cleanConjunto || cleanToken)) {
        const targetSlug = cleanConjunto || cleanToken;
        matched = list.find((a) => {
          const aSlug = PaymentAgreementPdfService.slugifyConjunto(a.copropiedad_nombre);
          const aNameLower = (a.copropiedad_nombre || '').toLowerCase();
          return (
            aSlug === targetSlug ||
            aSlug.includes(targetSlug) ||
            targetSlug.includes(aSlug) ||
            aNameLower.includes(targetSlug.replace(/-/g, ' '))
          );
        });
      }

      // 3. Fallback: Primer acuerdo
      const selected = matched || (list.length > 0 ? list[0] : null);

      if (selected) {
        setAgreement(selected);
        setPayerEmail(selected.email || 'carlos.restrepo@gmail.com');
        setNequiPhone(selected.telefono || '3104567890');
        
        const alreadySigned =
          Boolean(selected.firmado_digitalmente_at) ||
          (selected.estado !== 'enviado_al_deudor' && selected.estado !== 'borrador');
        setIsSigned(alreadySigned);
        if (selected.firmado_digitalmente_at) {
          setSignatureTimestamp(new Date(selected.firmado_digitalmente_at).toLocaleString('es-CO'));
        } else if (alreadySigned) {
          setSignatureTimestamp(new Date(selected.created_at || '2026-09-15T09:00:00Z').toLocaleString('es-CO'));
        }
      }
    } catch (err) {
      console.warn('Error loading agreements for debtor portal:', err);
      const fallback = FALLBACK_AGREEMENTS[0];
      setAgreement(fallback);
      setPayerEmail(fallback.email || '');
      setNequiPhone(fallback.telefono || '');
      setIsSigned(false);
    } finally {
      setIsLoading(false);
    }
  }, [activeToken, conjuntoSlug]);

  // Actualizar acuerdo en almacenamiento local y base de datos Supabase
  const updatePersistedAgreement = (updated: PaymentAgreement, auditPayload?: any) => {
    setAgreement(updated);
    const updatedList = allAgreements.map((a) => (a.id === updated.id ? updated : a));
    setAllAgreements(updatedList);
    // Persistencia centralizada en Supabase y local
    PaymentAgreementDatabaseService.persistAgreement(updated, auditPayload);
  };

  // Enviar código OTP
  const handleSendOtp = () => {
    if (!agreement) return;
    setIsOtpSent(true);
    setOtpCode('4819'); // Código sugerido prellenado para pruebas
    // Registrar auditoría de generación OTP
    PaymentAgreementDatabaseService.logAuditEvent({
      agreement_id: agreement.id,
      event: 'otp_generated',
      actor_type: 'debtor',
      metadata: { telefono: agreement.telefono, propietario: agreement.propietario_nombre }
    });
    toast.success(`Código de seguridad OTP enviado vía SMS/WhatsApp al ${agreement.telefono || 'móvil del deudor'}.`);
  };

  // Gestión de Canvas de firma manuscrita
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setIsDrawing(true);
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    ctx.beginPath();
    ctx.moveTo(clientX - rect.left, clientY - rect.top);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#047857'; // Verde esmeralda
    ctx.lineTo(clientX - rect.left, clientY - rect.top);
    ctx.stroke();
    setHasDrawnSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawnSignature(false);
  };

  // Formalizar y Firmar Digitalmente el Acuerdo
  const handleSignAgreement = () => {
    if (!agreement) return;

    if (!acceptedTerms) {
      toast.error('Debe marcar la casilla de aceptación legal de las condiciones del acuerdo.');
      return;
    }

    if (signatureMode === 'otp' && !otpCode.trim()) {
      toast.error('Por favor digite el código seguro OTP enviado a su celular.');
      return;
    }

    if (signatureMode === 'canvas' && !hasDrawnSignature) {
      toast.error('Por favor realice su trazo de firma manuscrita en el recuadro.');
      return;
    }

    const nowIso = new Date().toISOString();
    const formattedNow = new Date().toLocaleString('es-CO');

    const updated: PaymentAgreement = {
      ...agreement,
      estado: 'firmado_activo',
      firmado_digitalmente_at: nowIso,
      codigo_otp_firma: signatureMode === 'otp' ? otpCode : 'CANVAS_SIGNATURE',
      updated_at: nowIso
    };

    updatePersistedAgreement(updated, {
      agreement_id: updated.id,
      event: 'signed_digitally',
      actor_type: 'debtor',
      metadata: {
        propietario: updated.propietario_nombre,
        cedula: updated.propietario_id,
        unidad: updated.unidad_privada,
        metodo_firma: signatureMode,
        otp: signatureMode === 'otp' ? otpCode : 'TRAZO_CANVAS',
        ley: 'Ley 527 de 1999',
        merito_ejecutivo: 'Art. 2469 Código Civil y Ley 675/2001'
      }
    });
    setIsSigned(true);
    setSignatureTimestamp(formattedNow);
    toast.success('¡Acuerdo de Pago firmado electrónicamente con éxito bajo la Ley 527 de 1999!');
  };

  // Restablecer firma para demostración
  const handleResetSignature = () => {
    if (!agreement) return;
    const updated: PaymentAgreement = {
      ...agreement,
      estado: 'enviado_al_deudor',
      firmado_digitalmente_at: undefined,
      cuota_inicial_pagada: false
    };
    updatePersistedAgreement(updated);
    setIsSigned(false);
    setAcceptedTerms(false);
    setOtpCode('');
    setIsOtpSent(false);
    clearCanvas();
    toast.info('Modo de firma reiniciado para demostración.');
  };

  // Simulación de Pago Wompi / PSE / Nequi / Bancos
  const handleExecutePayment = () => {
    if (!agreement) return;
    if (!isSigned) {
      toast.error('Debe firmar y formalizar el acuerdo antes de realizar el primer pago.');
      return;
    }

    setIsProcessingPayment(true);
    setTimeout(() => {
      setIsProcessingPayment(false);
      setIsWaitingNequiPush(false);

      const nextPendingIdx = agreement.cuotas_detalle.findIndex(
        (c) => c.estado === 'vencida' || c.estado === 'pendiente'
      );
      const amountToPay = !agreement.cuota_inicial_pagada
        ? agreement.cuota_inicial_monto
        : nextPendingIdx !== -1
        ? agreement.cuotas_detalle[nextPendingIdx].total_cuota
        : agreement.valor_cuota_mensual;

      let prefix = 'PSE-COL';
      let bankLabel = 'Bancolombia';

      if (paymentMethod === 'wompi') {
        prefix = 'WOMPI-COL';
        bankLabel = wompiSubMethod === 'bancolombia' ? 'Botón Bancolombia (Wompi)' : wompiSubMethod.toUpperCase();
      } else if (paymentMethod === 'nequi') {
        prefix = 'NEQUI';
        bankLabel = 'Nequi Colombia';
      } else if (paymentMethod === 'pse') {
        prefix = 'PSE-COL';
        bankLabel = selectedBank;
      } else {
        prefix = 'BANCOL';
        bankLabel = 'Corresponsal Bancolombia';
      }

      const ref = `${prefix}-${Math.floor(100000 + Math.random() * 900000)}`;

      let cuotaInicialPagada = agreement.cuota_inicial_pagada;
      let cuotasPagadas = agreement.cuotas_pagadas;
      const cuotasDetalle = [...agreement.cuotas_detalle];

      if (!cuotaInicialPagada) {
        cuotaInicialPagada = true;
      } else {
        const nextPendingIdx = cuotasDetalle.findIndex(
          (c) => c.estado === 'pendiente' || c.estado === 'vencida'
        );
        if (nextPendingIdx !== -1) {
          cuotasDetalle[nextPendingIdx] = {
            ...cuotasDetalle[nextPendingIdx],
            estado: 'pagada',
            fecha_pago: new Date().toISOString().split('T')[0],
            referencia_pago: ref
          };
          cuotasPagadas = Math.min(agreement.cuotas_totales, cuotasPagadas + 1);
        }
      }

      const nuevoRecaudado = agreement.total_recaudado + amountToPay;
      const nuevoSaldo = Math.max(0, agreement.deuda_pactada - nuevoRecaudado);
      const nuevoEstado: AgreementStatus = nuevoSaldo <= 0 ? 'cumplido' : 'al_dia';

      const nextPending = cuotasDetalle.find((c) => c.estado === 'pendiente');
      const proxVenc = nextPending ? nextPending.fecha_vencimiento : 'ACUERDO CUMPLIDO';

      const updatedAgreement: PaymentAgreement = {
        ...agreement,
        cuota_inicial_pagada: cuotaInicialPagada,
        cuotas_pagadas: cuotasPagadas,
        total_recaudado: nuevoRecaudado,
        saldo_restante: nuevoSaldo,
        estado: nuevoEstado,
        proximo_vencimiento: proxVenc,
        cuotas_detalle: cuotasDetalle,
        updated_at: new Date().toISOString()
      };

      updatePersistedAgreement(updatedAgreement, {
        agreement_id: updatedAgreement.id,
        event: 'payment_received',
        actor_type: paymentMethod === 'wompi' ? 'gateway_wompi' : paymentMethod === 'nequi' ? 'gateway_nequi' : 'debtor',
        metadata: {
          referencia: ref,
          monto: amountToPay,
          pasarela: paymentMethod,
          banco: bankLabel,
          nuevo_saldo: nuevoSaldo,
          estado_resultante: nuevoEstado
        }
      });

      setPaymentReceipt({
        referencia: ref,
        fecha: new Date().toLocaleString('es-CO'),
        monto: amountToPay,
        metodo: paymentMethod.toUpperCase(),
        banco: bankLabel
      });

      toast.success(`¡Pago por $${amountToPay.toLocaleString('es-CO')} COP acreditado exitosamente!`);
    }, 1400);
  };

  // Simulación Push de Nequi
  const handleTriggerNequiPush = () => {
    if (!isSigned) {
      toast.error('Debe firmar el acuerdo antes de realizar el pago.');
      return;
    }
    setIsWaitingNequiPush(true);
    toast.info(`Notificación Push enviada a la app Nequi vinculada al número ${nequiPhone}. Abre Nequi para autorizar.`);
    setTimeout(() => {
      handleExecutePayment();
    }, 2800);
  };

  const currentAgreement = agreement || FALLBACK_AGREEMENTS[0];
  const ahorro = currentAgreement.intereses_condonados || 0;

  // Próxima cuota pendiente o en mora
  const nextPendingCuota = (currentAgreement.cuotas_detalle || []).find(
    (c) => c.estado === 'vencida' || c.estado === 'pendiente'
  );

  const currentAmountToPay = !currentAgreement.cuota_inicial_pagada
    ? currentAgreement.cuota_inicial_monto
    : nextPendingCuota
    ? nextPendingCuota.total_cuota
    : currentAgreement.valor_cuota_mensual;

  const isFullyPaid = currentAgreement.saldo_restante <= 0 || currentAgreement.estado === 'cumplido';

  return (
    <div className="min-h-screen bg-slate-100/70 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col font-sans">
      {/* ── BARRA SUPERIOR INSTITUCIONAL EXCLUSIVA DEL DEUDOR ── */}
      <header className="bg-slate-900 text-white border-b border-slate-800 shadow-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-tr from-emerald-500 to-teal-400 flex items-center justify-center text-white shadow-md">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-sm tracking-wide uppercase">
                  {currentAgreement.copropiedad_nombre}
                </span>
                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px] gap-1">
                  <ShieldCheck className="h-3 w-3 inline" /> Portal Seguro SSL
                </Badge>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Portal de Autogestión y Pagos de Cartera &bull; {currentAgreement.unidad_privada} ({currentAgreement.propietario_nombre})
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-center flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => PaymentAgreementPdfService.downloadAgreementPdf(currentAgreement)}
              className="h-8 text-xs gap-1.5 border-emerald-500/60 bg-emerald-950/40 text-emerald-300 hover:bg-emerald-900/60 font-semibold"
              title="Descargar Contrato Oficial en Formato PDF"
            >
              <Download className="h-3.5 w-3.5" /> Descargar Contrato (PDF)
            </Button>
          </div>
        </div>
      </header>

      {/* ── CUERPO PRINCIPAL DEL PORTAL ── */}
      <main className="flex-1 max-w-6xl w-full mx-auto p-4 sm:p-6 space-y-6">
        {paymentReceipt ? (
          /* ── PANTALLA DE COMPROBANTE OFICIAL DE RECAUDO ── */
          <div className="max-w-md mx-auto text-center space-y-5 py-8">
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto shadow-inner animate-bounce">
              <CheckCircle2 className="h-10 w-10" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                ¡Transacción Exitosa!
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                El pago ha sido registrado y aplicado a la cuenta del inmueble {currentAgreement.unidad_privada}.
              </p>
            </div>

            <Card className="border border-emerald-200 dark:border-emerald-900/60 shadow-md text-left bg-white dark:bg-slate-900">
              <CardHeader className="pb-2 border-b">
                <CardTitle className="text-xs font-semibold uppercase text-muted-foreground flex items-center justify-between">
                  <span>Comprobante Oficial de Recaudo</span>
                  <Badge variant="outline" className="border-emerald-300 text-emerald-600 text-[10px] font-bold">
                    APROBADO
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2.5 pt-3 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-500">Referencia de Pago:</span>
                  <span className="font-mono font-bold text-slate-800 dark:text-slate-200">{paymentReceipt.referencia}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Monto Acreditado:</span>
                  <span className="font-bold text-emerald-600 text-sm">${paymentReceipt.monto.toLocaleString('es-CO')} COP</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Fecha y Hora:</span>
                  <span>{paymentReceipt.fecha}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Canal / Pasarela:</span>
                  <span className="font-medium">{paymentReceipt.metodo} ({paymentReceipt.banco})</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Inmueble Beneficiario:</span>
                  <span className="font-semibold">{currentAgreement.unidad_privada}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Saldo Restante:</span>
                  <span className="font-bold text-amber-600">${currentAgreement.saldo_restante.toLocaleString('es-CO')} COP</span>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-2 justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => PaymentAgreementPdfService.downloadAgreementPdf(currentAgreement)}
                className="gap-1.5 text-xs h-9 border-emerald-500 text-emerald-700 hover:bg-emerald-50 font-semibold"
              >
                <Download className="h-3.5 w-3.5" /> Descargar Acuerdo Actualizado (PDF)
              </Button>
              <Button
                size="sm"
                onClick={() => setPaymentReceipt(null)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9 font-semibold"
              >
                Continuar en el Portal
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* ── COLUMNA IZQUIERDA: RESUMEN FINANCIERO Y CRONOGRAMA DE CUOTAS ── */}
            <div className="lg:col-span-6 space-y-4">
              {/* Tarjeta de Ahorro y Deuda Pactada */}
              <Card className="border border-emerald-200/80 dark:border-emerald-900/50 bg-gradient-to-br from-emerald-50/90 via-teal-50/50 to-white dark:from-emerald-950/30 dark:via-slate-900 dark:to-slate-900 shadow-sm overflow-hidden">
                <CardContent className="p-4 sm:p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
                      Propuesta de Acuerdo Formal
                    </span>
                    {ahorro > 0 && (
                      <Badge className="bg-emerald-600 text-white text-[10px] font-bold">
                        Descuento del {currentAgreement.porcentaje_condonacion_intereses}% en Intereses
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div>
                      <span className="text-[11px] text-slate-500 block">Deuda Original Acumulada</span>
                      <span className="text-sm font-semibold line-through text-slate-400">
                        ${currentAgreement.deuda_total_inicial.toLocaleString('es-CO')}
                      </span>
                    </div>
                    <div>
                      <span className="text-[11px] text-emerald-700 dark:text-emerald-400 font-medium block">
                        Total a Pagar con Descuento
                      </span>
                      <span className="text-xl font-black text-emerald-900 dark:text-emerald-100">
                        ${currentAgreement.deuda_pactada.toLocaleString('es-CO')}
                      </span>
                    </div>
                  </div>

                  {ahorro > 0 && (
                    <div className="p-2.5 rounded-lg bg-emerald-100/70 dark:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-900 dark:text-emerald-200 flex items-center justify-between">
                      <span>Ahorro obtenido por acuerdo:</span>
                      <strong className="font-bold text-emerald-800 dark:text-emerald-300">
                        ${ahorro.toLocaleString('es-CO')} COP
                      </strong>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Resumen del Plan de Pagos / Financiación */}
              <Card className="border shadow-sm bg-white dark:bg-slate-900">
                <CardHeader className="pb-3 border-b bg-muted/20">
                  <CardTitle className="text-xs font-bold uppercase tracking-wider flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5 text-orange-600" />
                      <span>Estructura de Cuotas y Financiación</span>
                    </div>
                    <span className="text-[11px] text-muted-foreground font-normal">
                      {currentAgreement.numero_cuotas} cuotas de ${currentAgreement.valor_cuota_mensual.toLocaleString('es-CO')}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-100 dark:divide-slate-800 text-xs max-h-64 overflow-y-auto">
                    {/* Cuota Inicial */}
                    <div className="p-3 flex items-center justify-between bg-amber-50/50 dark:bg-amber-950/20">
                      <div>
                        <div className="font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
                          <span>Cuota Inicial ({currentAgreement.cuota_inicial_porcentaje}%)</span>
                          {currentAgreement.cuota_inicial_pagada ? (
                            <Badge className="bg-emerald-600 text-white text-[9px] h-4 px-1.5">PAGADA</Badge>
                          ) : (
                            <Badge variant="outline" className="border-amber-400 text-amber-700 text-[9px] h-4 px-1.5 font-bold">PENDIENTE</Badge>
                          )}
                        </div>
                        <span className="text-[10px] text-muted-foreground">
                          Límite: {currentAgreement.cuota_inicial_fecha_limite}
                        </span>
                      </div>
                      <span className="font-bold text-sm text-slate-800 dark:text-slate-200">
                        ${currentAgreement.cuota_inicial_monto.toLocaleString('es-CO')}
                      </span>
                    </div>

                    {/* Cuotas Mensuales Financiadas */}
                    {currentAgreement.cuotas_detalle.map((cuota) => (
                      <div key={cuota.numero} className="p-3 flex items-center justify-between hover:bg-muted/30 transition-colors">
                        <div className="space-y-0.5">
                          <div className="font-medium flex items-center gap-1.5">
                            <span>Cuota #{cuota.numero} de {currentAgreement.numero_cuotas}</span>
                            <Badge
                              variant="outline"
                              className={`text-[9px] h-4 px-1.5 ${
                                cuota.estado === 'pagada'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                                  : cuota.estado === 'vencida'
                                  ? 'bg-rose-50 text-rose-700 border-rose-300'
                                  : 'text-slate-600 border-slate-300'
                              }`}
                            >
                              {cuota.estado.toUpperCase()}
                            </Badge>
                          </div>
                          <span className="text-[10px] text-slate-500">
                            Vence: {cuota.fecha_vencimiento}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-slate-800 dark:text-slate-200 block">
                            ${cuota.total_cuota.toLocaleString('es-CO')}
                          </span>
                          {cuota.referencia_pago && (
                            <span className="text-[9px] text-emerald-600 font-mono">
                              Ref: {cuota.referencia_pago}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Cláusula Jurídica Resumida */}
              <div className="p-3 rounded-lg border bg-blue-50/60 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900/50 text-[11px] text-blue-950 dark:text-blue-200 space-y-1">
                <div className="flex items-center gap-1.5 font-bold text-blue-900 dark:text-blue-300">
                  <Lock className="h-3.5 w-3.5" />
                  Validez Legal del Acuerdo (Art. 2469 Código Civil y Ley 675)
                </div>
                <p className="leading-tight text-blue-800 dark:text-blue-300 text-[10px]">
                  El presente acuerdo presta mérito ejecutivo pleno. El pago cumplido de las cuotas suspende cualquier proceso de cobro coactivo. En caso de incumplimiento de una sola cuota, se activa la cláusula aceleratoria sobre el saldo total sin derecho a la condonación de intereses.
                </p>
              </div>
            </div>

            {/* ── COLUMNA DERECHA: FIRMA DIGITAL Y PASARELA DE PAGO ── */}
            <div className="lg:col-span-6 space-y-4">
              {!isSigned ? (
                /* ── MODO 1: ACUERDO NUEVO PENDIENTE DE FIRMA (PRIMERA VEZ) ── */
                <>
                  <Card className="border shadow-sm transition-all bg-white dark:bg-slate-900 border-amber-300">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-xs font-bold uppercase tracking-wider flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold bg-amber-600 text-white">
                            1
                          </div>
                          <span>Aceptación y Firma Digital del Acuerdo</span>
                        </div>
                        <Badge variant="outline" className="border-amber-500 text-amber-700 text-[10px] animate-pulse">
                          PENDIENTE DE FIRMA
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 pt-1">
                      <div className="space-y-3">
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          Para formalizar este acuerdo ante la administración de <strong>{currentAgreement.copropiedad_nombre}</strong>, confirme su aceptación bajo los términos de la <strong>Ley 527 de 1999</strong> (Comercio Electrónico y Firmas Digitales).
                        </p>

                        {/* Selector de Método de Firma: OTP o Trazo en Pantalla */}
                        <div className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-[11px]">
                          <button
                            type="button"
                            onClick={() => setSignatureMode('otp')}
                            className={`py-1.5 px-2 rounded-md font-medium transition-all ${
                              signatureMode === 'otp'
                                ? 'bg-white dark:bg-slate-900 text-emerald-700 dark:text-emerald-300 shadow-xs'
                                : 'text-slate-600 hover:text-slate-900'
                            }`}
                          >
                            📲 Código OTP (SMS / Celular)
                          </button>
                          <button
                            type="button"
                            onClick={() => setSignatureMode('canvas')}
                            className={`py-1.5 px-2 rounded-md font-medium transition-all ${
                              signatureMode === 'canvas'
                                ? 'bg-white dark:bg-slate-900 text-emerald-700 dark:text-emerald-300 shadow-xs'
                                : 'text-slate-600 hover:text-slate-900'
                            }`}
                          >
                            ✍️ Firma en Pantalla (Dedo/Mouse)
                          </button>
                        </div>

                        {signatureMode === 'otp' ? (
                          <div className="space-y-2 p-3 rounded-lg border bg-amber-50/40 dark:bg-amber-950/20 border-amber-200">
                            <Label className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                              Verificación por Código de Seguridad OTP
                            </Label>
                            <div className="flex items-center gap-2">
                              <Input
                                placeholder="Código OTP (ej. 4819)"
                                value={otpCode}
                                onChange={(e) => setOtpCode(e.target.value)}
                                className="text-xs h-9 font-mono max-w-[160px] bg-white"
                              />
                              {!isOtpSent ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={handleSendOtp}
                                  className="text-xs h-9 gap-1 border-amber-400 text-amber-800 hover:bg-amber-100"
                                >
                                  <Send className="h-3 w-3" /> Enviar Código al Celular
                                </Button>
                              ) : (
                                <span className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1">
                                  <Check className="h-3.5 w-3.5" /> Código Enviado
                                </span>
                              )}
                            </div>
                            <span className="text-[10px] text-slate-500 block">
                              Se enviará al número registrado: <strong>{currentAgreement.telefono}</strong>
                            </span>
                          </div>
                        ) : (
                          <div className="space-y-2 p-3 rounded-lg border bg-slate-50 dark:bg-slate-800/50">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1">
                                <PenTool className="h-3.5 w-3.5 text-emerald-600" />
                                Dibuje su firma en el recuadro inferior
                              </Label>
                              <button
                                type="button"
                                onClick={clearCanvas}
                                className="text-[10px] text-slate-500 hover:text-rose-600 flex items-center gap-0.5"
                              >
                                <RotateCcw className="h-3 w-3" /> Limpiar
                              </button>
                            </div>
                            <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-md bg-white overflow-hidden shadow-inner">
                              <canvas
                                ref={canvasRef}
                                width={420}
                                height={110}
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                them="touch-none"
                                onTouchStart={startDrawing}
                                onTouchMove={draw}
                                onTouchEnd={stopDrawing}
                                className="w-full h-24 touch-none cursor-crosshair"
                              />
                            </div>
                            <span className="text-[10px] text-slate-400 block text-center">
                              Use el mouse o el dedo sobre la pantalla para trazar su firma manuscrita.
                            </span>
                          </div>
                        )}

                        {/* Casilla de Aceptación Legal Vinculante */}
                        <label className="flex items-start gap-2 pt-1 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={acceptedTerms}
                            onChange={(e) => setAcceptedTerms(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <span className="text-[11px] text-slate-700 dark:text-slate-300 leading-snug">
                            Declaro que he leído el acuerdo, reconozco la deuda libre y voluntariamente y acepto el cronograma de cuotas pactado con mérito ejecutivo pleno (Art. 2469 C.C. y Ley 675/2001).
                          </span>
                        </label>

                        <Button
                          type="button"
                          onClick={handleSignAgreement}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-10 shadow-md gap-2"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          Firmar y Formalizar Acuerdo de Pago
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Paso 2 Bloqueado hasta firmar */}
                  <Card className="border shadow-sm opacity-60 pointer-events-none bg-white dark:bg-slate-900">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-xs font-bold uppercase tracking-wider flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-slate-400 text-white flex items-center justify-center text-[10px] font-bold">
                            2
                          </div>
                          <span>Pagar en Línea (Se desbloquea al firmar)</span>
                        </div>
                        <span className="text-xs font-bold text-slate-400">
                          Cuota Inicial: ${currentAgreement.cuota_inicial_monto.toLocaleString('es-CO')}
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="py-6 text-center text-xs text-muted-foreground">
                      <Lock className="h-6 w-6 mx-auto mb-2 text-slate-400" />
                      Debe completar la firma digital del acuerdo en el Paso 1 para habilitar la pasarela de pago.
                    </CardContent>
                  </Card>
                </>
              ) : (
                /* ── MODO 2: ACUERDO YA FIRMADO (DIAS O MESES DESPUES) ── */
                <>
                  {/* Certificado de Firma Registrada (Sin formulario de firma) */}
                  <Card className="border border-emerald-300 dark:border-emerald-800 bg-gradient-to-br from-emerald-50 via-teal-50/40 to-white dark:from-emerald-950/40 dark:via-slate-900 dark:to-slate-900 shadow-sm">
                    <CardContent className="p-3.5 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-xs">
                            <ShieldCheck className="h-4 w-4" />
                          </div>
                          <div>
                            <span className="font-bold text-xs text-emerald-950 dark:text-emerald-100 block">
                              Acuerdo Formalizado y Firmado Electrónicamente
                            </span>
                            <span className="text-[10px] text-emerald-700 dark:text-emerald-300">
                              Presta mérito ejecutivo pleno (Art. 2469 C.C. y Ley 527 de 1999)
                            </span>
                          </div>
                        </div>
                        <Badge className="bg-emerald-600 text-white text-[10px] font-bold">
                          FIRMADO
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-emerald-200/60 dark:border-emerald-800/60 text-[11px]">
                        <div>
                          <span className="text-slate-500 block text-[10px]">Titular:</span>
                          <strong className="text-slate-800 dark:text-slate-200">{currentAgreement.propietario_nombre}</strong>
                        </div>
                        <div>
                          <span className="text-slate-500 block text-[10px]">Fecha de Aceptación:</span>
                          <strong className="text-slate-800 dark:text-slate-200">{signatureTimestamp || '15/09/2026, 09:00:00'}</strong>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Si ya pagó la totalidad: Certificado de Paz y Salvo */}
                  {isFullyPaid ? (
                    <Card className="border border-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/20 text-center p-6 space-y-3 shadow-sm">
                      <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-inner">
                        <CheckCircle2 className="h-8 w-8" />
                      </div>
                      <div>
                        <h4 className="text-base font-bold text-emerald-900 dark:text-emerald-100">
                          ¡Acuerdo de Pago Cumplido al 100%!
                        </h4>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 max-w-sm mx-auto">
                          Ha cancelado la totalidad de las cuotas pactadas. Su inmueble {currentAgreement.unidad_privada} se encuentra al día y a Paz y Salvo con la administración.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => PaymentAgreementPdfService.downloadAgreementPdf(currentAgreement)}
                        className="text-xs gap-1.5 border-emerald-500 text-emerald-700 hover:bg-emerald-100"
                      >
                        <Download className="h-3.5 w-3.5" /> Descargar Certificado de Paz y Salvo (PDF)
                      </Button>
                    </Card>
                  ) : (
                    /* Si tiene cuotas pendientes: Pasarela de Recaudo Directa */
                    <Card className="border shadow-sm bg-white dark:bg-slate-900">
                      <CardHeader className="pb-2 border-b bg-slate-50/60 dark:bg-slate-800/40">
                        <CardTitle className="text-xs font-bold uppercase tracking-wider flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CreditCard className="h-4 w-4 text-emerald-600" />
                            <span>Portal de Recaudo y Pagos en Línea</span>
                          </div>
                          <div className="text-right">
                            {!currentAgreement.cuota_inicial_pagada ? (
                              <Badge variant="outline" className="border-amber-400 text-amber-700 text-[10px] font-bold">
                                CUOTA INICIAL: ${currentAgreement.cuota_inicial_monto.toLocaleString('es-CO')}
                              </Badge>
                            ) : nextPendingCuota ? (
                              <Badge
                                className={`text-[10px] font-bold ${
                                  nextPendingCuota.estado === 'vencida'
                                    ? 'bg-rose-600 text-white'
                                    : 'bg-emerald-600 text-white'
                                }`}
                              >
                                {nextPendingCuota.estado === 'vencida' ? 'CUOTA EN MORA' : 'CUOTA AL DÍA'} (#{nextPendingCuota.numero} de {currentAgreement.cuotas_totales}): ${nextPendingCuota.total_cuota.toLocaleString('es-CO')}
                              </Badge>
                            ) : (
                              <Badge className="bg-emerald-600 text-white text-[10px] font-bold">
                                CUOTA ORDINARIA: ${currentAgreement.valor_cuota_mensual.toLocaleString('es-CO')}
                              </Badge>
                            )}
                          </div>
                        </CardTitle>
                        {nextPendingCuota && (
                          <CardDescription className="text-[11px] pt-1">
                            {nextPendingCuota.estado === 'vencida'
                              ? `⚠️ Esta cuota venció el ${nextPendingCuota.fecha_vencimiento}. Pague en línea para normalizar su estado.`
                              : `Fecha límite de pago: ${nextPendingCuota.fecha_vencimiento}`}
                          </CardDescription>
                        )}
                      </CardHeader>
                      <CardContent className="space-y-4 pt-3">
                        <Tabs value={paymentMethod} onValueChange={(val: any) => setPaymentMethod(val)}>
                          {/* Pestañas: Wompi, PSE, Nequi, Bancos */}
                          <TabsList className="grid grid-cols-4 w-full h-8 text-[11px]">
                            <TabsTrigger value="wompi" className="text-[11px] gap-1 font-semibold text-purple-700 dark:text-purple-300">
                              ⚡ Wompi
                            </TabsTrigger>
                            <TabsTrigger value="pse" className="text-[11px] gap-1">
                              <CreditCard className="h-3 w-3" /> PSE
                            </TabsTrigger>
                            <TabsTrigger value="nequi" className="text-[11px] gap-1 text-pink-600 dark:text-pink-400">
                              <Smartphone className="h-3 w-3" /> Nequi
                            </TabsTrigger>
                            <TabsTrigger value="barras" className="text-[11px] gap-1">
                              <Receipt className="h-3 w-3" /> Bancos
                            </TabsTrigger>
                          </TabsList>

                          <div className="pt-3">
                            {/* Pestaña WOMPI (Bancolombia) */}
                            <TabsContent value="wompi" className="space-y-3 m-0">
                              <div className="p-3 rounded-lg border border-purple-200 dark:border-purple-900/60 bg-gradient-to-r from-purple-50/70 via-fuchsia-50/40 to-white dark:from-purple-950/20 dark:to-slate-900 space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="font-bold text-xs text-purple-950 dark:text-purple-200 flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-teal-400 animate-ping"></span>
                                    Pasarela Oficial Wompi Bancolombia
                                  </span>
                                  <Badge className="bg-purple-900 text-white text-[9px] font-mono">
                                    WOMPI V2
                                  </Badge>
                                </div>
                                <p className="text-[11px] text-purple-900/80 dark:text-purple-300/80 leading-tight">
                                  Pague de forma instantánea a través de Wompi con débito a cuenta Bancolombia, PSE o billetera digital sin comisiones.
                                </p>

                                <div className="grid grid-cols-3 gap-2 pt-1">
                                  <button
                                    type="button"
                                    onClick={() => setWompiSubMethod('bancolombia')}
                                    className={`p-2 rounded border text-center text-[10px] font-semibold transition-all ${
                                      wompiSubMethod === 'bancolombia'
                                        ? 'border-purple-600 bg-purple-600 text-white shadow-xs'
                                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                                    }`}
                                  >
                                    Botón Bancolombia
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setWompiSubMethod('pse')}
                                    className={`p-2 rounded border text-center text-[10px] font-semibold transition-all ${
                                      wompiSubMethod === 'pse'
                                        ? 'border-purple-600 bg-purple-600 text-white shadow-xs'
                                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                                    }`}
                                  >
                                    PSE Wompi
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setWompiSubMethod('nequi')}
                                    className={`p-2 rounded border text-center text-[10px] font-semibold transition-all ${
                                      wompiSubMethod === 'nequi'
                                        ? 'border-purple-600 bg-purple-600 text-white shadow-xs'
                                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                                    }`}
                                  >
                                    Nequi Wompi
                                  </button>
                                </div>
                              </div>

                              <Button
                                onClick={handleExecutePayment}
                                disabled={isProcessingPayment}
                                className="w-full bg-purple-900 hover:bg-purple-950 text-white font-bold text-xs h-10 shadow-md gap-2"
                              >
                                {isProcessingPayment ? (
                                  <>Conectando con Checkout Wompi Seguro...</>
                                ) : (
                                  <>
                                    <Lock className="h-3.5 w-3.5" />
                                    Pagar ${currentAmountToPay.toLocaleString('es-CO')} COP con Wompi
                                  </>
                                )}
                              </Button>
                            </TabsContent>

                            {/* Pestaña PSE */}
                            <TabsContent value="pse" className="space-y-3 m-0">
                              <div className="space-y-1">
                                <Label className="text-xs">Seleccione su Entidad Financiera</Label>
                                <select
                                  value={selectedBank}
                                  onChange={(e) => setSelectedBank(e.target.value)}
                                  className="w-full text-xs h-9 rounded-md border border-input bg-background px-3 py-1 shadow-sm"
                                >
                                  <option value="Bancolombia">Bancolombia</option>
                                  <option value="Davivienda">Davivienda</option>
                                  <option value="Banco de Bogotá">Banco de Bogotá</option>
                                  <option value="BBVA Colombia">BBVA Colombia</option>
                                  <option value="Nequi (vía PSE)">Nequi (A través de PSE)</option>
                                  <option value="Daviplata">Daviplata</option>
                                  <option value="Banco de Occidente">Banco de Occidente</option>
                                  <option value="Scotiabank Colpatria">Scotiabank Colpatria</option>
                                </select>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Correo Electrónico Registrado en PSE</Label>
                                <Input
                                  type="email"
                                  value={payerEmail}
                                  onChange={(e) => setPayerEmail(e.target.value)}
                                  className="text-xs h-8"
                                />
                              </div>

                              <Button
                                onClick={handleExecutePayment}
                                disabled={isProcessingPayment}
                                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-10 shadow-md gap-2"
                              >
                                {isProcessingPayment ? (
                                  <>Procesando con PSE Colombia...</>
                                ) : (
                                  <>
                                    <Lock className="h-3.5 w-3.5" />
                                    Pagar ${currentAmountToPay.toLocaleString('es-CO')} COP con PSE
                                  </>
                                )}
                              </Button>
                            </TabsContent>

                            {/* Pestaña NEQUI */}
                            <TabsContent value="nequi" className="space-y-3 m-0 text-center py-1">
                              <div className="p-3 rounded-lg border border-pink-200 dark:border-pink-900 bg-pink-50/50 dark:bg-pink-950/20 space-y-2.5">
                                <div className="flex items-center justify-between text-left">
                                  <div>
                                    <span className="font-bold text-xs text-pink-950 dark:text-pink-200 block">
                                      Pago Directo con Nequi
                                    </span>
                                    <span className="text-[10px] text-slate-500">
                                      Total: ${currentAmountToPay.toLocaleString('es-CO')} COP
                                    </span>
                                  </div>
                                  <Badge className="bg-pink-600 text-white text-[9px] font-bold">
                                    QR DINÁMICO
                                  </Badge>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
                                  <div className="p-2 rounded-lg border bg-white inline-block shadow-sm mx-auto text-center">
                                    <svg viewBox="0 0 100 100" className="w-24 h-24 mx-auto text-slate-900">
                                      <rect width="100" height="100" fill="white" />
                                      <rect x="5" y="5" width="25" height="25" fill="#200020" />
                                      <rect x="8" y="8" width="19" height="19" fill="white" />
                                      <rect x="11" y="11" width="13" height="13" fill="#da0081" />
                                      <rect x="70" y="5" width="25" height="25" fill="#200020" />
                                      <rect x="73" y="8" width="19" height="19" fill="white" />
                                      <rect x="76" y="11" width="13" height="13" fill="#da0081" />
                                      <rect x="5" y="70" width="25" height="25" fill="#200020" />
                                      <rect x="8" y="73" width="19" height="19" fill="white" />
                                      <rect x="11" y="76" width="13" height="13" fill="#da0081" />
                                      <rect x="36" y="15" width="8" height="8" fill="#200020" />
                                      <rect x="48" y="10" width="8" height="12" fill="#200020" />
                                      <rect x="40" y="35" width="20" height="20" fill="#da0081" rx="2" />
                                      <rect x="45" y="40" width="10" height="10" fill="white" />
                                      <rect x="15" y="45" width="12" height="6" fill="#200020" />
                                      <rect x="75" y="45" width="14" height="10" fill="#200020" />
                                      <rect x="35" y="70" width="10" height="15" fill="#200020" />
                                      <rect x="55" y="75" width="12" height="12" fill="#200020" />
                                      <rect x="75" y="70" width="15" height="15" fill="#200020" />
                                    </svg>
                                    <span className="text-[9px] font-mono text-pink-700 block mt-1 font-bold">
                                      Ref: NEQ-{currentAgreement.unidad_privada.replace(/\D/g, '') || '504'}
                                    </span>
                                  </div>

                                  <div className="space-y-2 text-left">
                                    <div className="space-y-1">
                                      <Label className="text-[11px] font-semibold">Número de Celular Nequi</Label>
                                      <Input
                                        value={nequiPhone}
                                        onChange={(e) => setNequiPhone(e.target.value)}
                                        placeholder="3101234567"
                                        className="h-8 text-xs font-mono bg-white"
                                      />
                                    </div>
                                    <p className="text-[10px] text-slate-500 leading-tight">
                                      Escanee el código QR desde su app o solicite notificación push para autorizar sin salir del celular.
                                    </p>
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                                  <Button
                                    type="button"
                                    onClick={handleTriggerNequiPush}
                                    disabled={isWaitingNequiPush || isProcessingPayment}
                                    className="bg-pink-600 hover:bg-pink-700 text-white text-xs h-9 font-semibold gap-1.5"
                                  >
                                    {isWaitingNequiPush ? (
                                      <>📲 Esperando en tu App...</>
                                    ) : (
                                      <>📲 Enviar Push a mi Nequi</>
                                    )}
                                  </Button>

                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleExecutePayment}
                                    disabled={isProcessingPayment}
                                    className="border-pink-300 text-pink-700 hover:bg-pink-100 text-xs h-9 font-semibold gap-1.5"
                                  >
                                    <Check className="h-3.5 w-3.5" /> Ya Pagué con el QR
                                  </Button>
                                </div>
                              </div>
                            </TabsContent>

                            {/* Pestaña Bancos */}
                            <TabsContent value="barras" className="space-y-2 m-0 text-xs">
                              <div className="p-3 rounded-lg border bg-slate-100 dark:bg-slate-800 space-y-1">
                                <div className="font-semibold text-slate-800 dark:text-slate-200">Convenio de Recaudo Bancolombia</div>
                                <div className="font-mono text-xs">Convenio No: <strong>48912</strong></div>
                                <div className="font-mono text-xs">Referencia: <strong>{currentAgreement.unidad_privada.replace(/\D/g, '') || '504'}</strong></div>
                                <p className="text-[10px] text-slate-500 pt-1">
                                  Presente este convenio y referencia en cualquier cajero o corresponsal bancario del país.
                                </p>
                              </div>

                              <Button
                                onClick={handleExecutePayment}
                                className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs h-9"
                              >
                                Registrar Pago por Corresponsal Bancario
                              </Button>
                            </TabsContent>
                          </div>
                        </Tabs>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {/* Botón de Ayuda y Contacto con Administración */}
              <div className="p-3.5 rounded-xl border bg-gradient-to-r from-slate-50 to-emerald-50/40 dark:from-slate-900 dark:to-emerald-950/20 border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs shadow-xs">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
                    <HelpCircle className="h-4 w-4" />
                  </div>
                  <div>
                    <span className="font-semibold text-slate-800 dark:text-slate-200 block">¿Dudas sobre su acuerdo?</span>
                    <span className="text-[11px] text-muted-foreground block">Comuníquese con los canales oficiales de administración</span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsContactModalOpen(true)}
                  className="text-xs border-emerald-300 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-950/60 font-semibold gap-1.5 h-8 shadow-xs"
                >
                  <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
                  Contactar Administración <ChevronRight className="h-3 w-3 inline" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* ── MODAL: ATENCIÓN AL COPROPIETARIO Y CONTACTO CON ADMINISTRACIÓN ── */}
      <Dialog open={isContactModalOpen} onOpenChange={setIsContactModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2.5">
              <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold text-slate-900 dark:text-white">
                  Atención al Copropietario
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {currentAgreement.copropiedad_nombre} &bull; NIT: {currentAgreement.copropiedad_nit || '900.852.147-3'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-3.5 py-2 text-xs">
            {/* Botón Principal de WhatsApp con la Administración */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/20 border border-emerald-200 dark:border-emerald-800/60 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="font-bold text-emerald-950 dark:text-emerald-200 text-xs flex items-center gap-1.5">
                  <MessageCircle className="h-4 w-4 text-emerald-600" />
                  Línea de WhatsApp de Cartera
                </span>
                <Badge className="bg-emerald-600 text-white text-[10px] font-mono">
                  +57 {currentAgreement.administrador_whatsapp || '3184567890'}
                </Badge>
              </div>
              <p className="text-[11px] text-slate-600 dark:text-slate-300 leading-snug">
                Escriba directamente a la administración. Su mensaje incluirá automáticamente su nombre, inmueble y radicado de acuerdo.
              </p>
              <a
                href={`https://wa.me/57${(currentAgreement.administrador_whatsapp || '3184567890').replace(/\D/g, '')}?text=${encodeURIComponent(
                  `Hola Administración de ${currentAgreement.copropiedad_nombre}, soy ${currentAgreement.propietario_nombre} del inmueble ${currentAgreement.unidad_privada}. Me comunico respecto a mi Acuerdo de Pago (${currentAgreement.id}).`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow transition-all hover:scale-[1.01]"
              >
                <MessageCircle className="h-4 w-4" />
                💬 Chatear por WhatsApp con Administración
              </a>
            </div>

            {/* Canales Adicionales */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <div className="p-3 rounded-lg border bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 space-y-1">
                <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400 font-semibold text-[11px]">
                  <Phone className="h-3.5 w-3.5 text-blue-600" />
                  Teléfono PBX / Fijo
                </div>
                <p className="font-mono font-medium text-slate-800 dark:text-slate-200">
                  {currentAgreement.administrador_telefono || '(601) 745-8900 Ext. 102'}
                </p>
              </div>

              <div className="p-3 rounded-lg border bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 space-y-1">
                <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400 font-semibold text-[11px]">
                  <Mail className="h-3.5 w-3.5 text-indigo-600" />
                  Correo de Cartera
                </div>
                <p className="font-mono font-medium text-slate-800 dark:text-slate-200 truncate">
                  {currentAgreement.administrador_email || 'cartera@torresdelparque.com'}
                </p>
              </div>

              <div className="p-3 rounded-lg border bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 space-y-1">
                <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400 font-semibold text-[11px]">
                  <Clock className="h-3.5 w-3.5 text-amber-600" />
                  Horario de Atención
                </div>
                <p className="text-slate-700 dark:text-slate-300 text-[11px] leading-tight">
                  {currentAgreement.administrador_horario || 'Lunes a Viernes 8:00 AM - 5:00 PM | Sábados 8:00 AM - 12:00 M'}
                </p>
              </div>

              <div className="p-3 rounded-lg border bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 space-y-1">
                <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400 font-semibold text-[11px]">
                  <MapPin className="h-3.5 w-3.5 text-rose-600" />
                  Oficina
                </div>
                <p className="text-slate-700 dark:text-slate-300 text-[11px] leading-tight">
                  {currentAgreement.administrador_oficina || 'Oficina de Administración - Torre 1, Piso 1'}
                </p>
              </div>
            </div>

            {/* Administrador responsable */}
            <div className="p-2.5 rounded-lg border bg-muted/40 flex items-center justify-between text-[11px]">
              <span className="text-muted-foreground">Administrador(a) Responsable:</span>
              <span className="font-semibold text-slate-800 dark:text-slate-200">
                {currentAgreement.administrador_nombre || 'Administración de la Copropiedad'}
              </span>
            </div>
          </div>

          <div className="pt-2 border-t flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsContactModalOpen(false)}
              className="text-xs h-8"
            >
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── PIE DE PÁGINA INSTITUCIONAL ── */}
      <footer className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 py-4 px-4 text-center text-xs text-slate-500">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>
            &copy; {new Date().getFullYear()} {currentAgreement.copropiedad_nombre} &bull; Portal de Cobranza y Recaudo PH
          </span>
          <span className="font-mono text-[11px]">
            Conforme a la Ley 675 de 2001, Código General del Proceso y Ley 2300 de 2023.
          </span>
        </div>
      </footer>
    </div>
  );
};
export default PublicDebtorPortalPage;
